import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import * as reportApi from "../api/requests/report";
import "./Reports.css";
import { Link } from "react-router-dom";
import Tabs from "../components/Tabs";
import { IonCard, IonCardContent } from "@ionic/react";
import { Helmet } from "react-helmet-async";

const ReportsTab = ({ resolved }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [currentPage]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await reportApi.getAllReports(resolved, currentPage, 20);
      if (response?.data) {
        setReports(response.data.reports);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsResolved = async (reportId) => {
    try {
      const response = await reportApi.markReportAsResolved(reportId);
      if (response?.data) {
        alert("Report marked as resolved");
        fetchReports();
      } else {
        alert(response?.data?.errors?.[0]?.msg || "Failed to mark as resolved");
      }
    } catch (error) {
      console.error("Error marking report as resolved:", error);
      alert("An error occurred while updating the report");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }
    try {
      const response = await reportApi.deleteReport(reportId);
      if (response?.data) {
        alert("Report deleted successfully");
        fetchReports();
      } else {
        alert(response?.data?.errors?.[0]?.msg || "Failed to delete report");
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      alert("An error occurred while deleting the report");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="reports-tab-content">
      {loading ? (
        <div className="reports-loading">Loading reports...</div>
      ) : reports.length === 0 ? (
        <div className="reports-empty">
          No {resolved ? "resolved" : "pending"} reports
        </div>
      ) : (
        <>
          <div>
            {reports.map((report) => (
              <IonCard key={report._id}>
                <IonCardContent>
                  <div className="report-body">
                    <div className="report-section">
                      <strong>Reported by:</strong>
                      <Link to={`/profile/${report.reporter.username}`}>
                        <div className="report-user">
                          <img
                            src={report.reporter.avatar || "/logo.png"}
                            alt={report.reporter.username}
                            className="report-avatar"
                          />
                          <span>{report.reporter.username}</span>
                        </div>
                      </Link>
                    </div>

                    <div className="report-section">
                      <strong>Reported User:</strong>
                      <Link to={`/profile/${report.reportedUser.username}`}>
                        <div className="report-user">
                          <img
                            src={report.reportedUser.avatar || "/logo.png"}
                            alt={report.reportedUser.username}
                            className="report-avatar"
                          />
                          <span>{report.reportedUser.username}</span>
                        </div>
                      </Link>
                    </div>

                    {report.reportedPost && (
                      <div className="report-section">
                        <strong>Reported Post:</strong>
                        <Link to={`/post/${report.reportedPost._id}`}>
                          View Post
                        </Link>
                      </div>
                    )}

                    <div className="report-section">
                      <strong>Reason:</strong>
                      <div className="report-reason">{report.reason}</div>
                    </div>

                    {report.additionalInfo && (
                      <div className="report-section">
                        <strong>Additional Info:</strong>
                        <div>{report.additionalInfo}</div>
                      </div>
                    )}
                  </div>

                  <div className="report-footer">
                    <div className="report-actions">
                      {!resolved && (
                        <button
                          className="report-action-btn resolve-btn"
                          onClick={() => handleMarkAsResolved(report._id)}
                        >
                          Mark as Resolved
                        </button>
                      )}
                      <button
                        className="report-action-btn delete-btn"
                        onClick={() => handleDeleteReport(report._id)}
                      >
                        Delete
                      </button>
                    </div>
                    <span className="report-date">
                      {formatDate(report.createdAt)}
                    </span>
                  </div>
                </IonCardContent>
              </IonCard>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="reports-pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {pagination.totalPages}
              </span>
              <button
                disabled={currentPage === pagination.totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PendingReports = () => <ReportsTab resolved={false} />;
const ResolvedReports = () => <ReportsTab resolved={true} />;

const Reports = () => {
  const currentUser = useSelector((state) => state.currentProfile.data);
  const isAdmin = currentUser?.isAdmin || false;

  if (!isAdmin) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div>
          <h2>Access Denied</h2>
          <p>You do not have permission to view this page.</p>
        </div>
      </>
    );
  }

  const tabs = [
    {
      label: "Pending",
      path: "",
      Component: PendingReports,
    },
    {
      label: "Resolved",
      path: "resolved",
      Component: ResolvedReports,
    },
  ];

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Tabs basePath="/reports" tabs={tabs} />
    </>
  );
};

export default Reports;
